
import React from "react";
import { useAuth } from "@/contexts/AuthContext";
import { TransactionList } from "@/components/transactions/TransactionList";
import { TransferForm } from "@/components/transactions/TransferForm";
import { ChildBalanceCard } from "./ChildBalanceCard";
import { TransferStatusCard } from "./TransferStatusCard";
import { TransferHistorySection } from "./TransferHistorySection";
import { useChildViewData } from "@/hooks/useChildViewData";
import { formatCurrency } from "@/lib/utils";

export function ChildView() {
  const { currentUser } = useAuth();
  const {
    transactions,
    transfers,
    familyMembers,
    isLoading,
    canTransfer,
    transfersError,
    handleTransfer
  } = useChildViewData();

  if (!currentUser) return null;

  const pendingTransfers = transfers.filter(t => t.status === 'pending');

  const getUserName = (userId: string) => {
    const user = familyMembers.find(u => u.id === userId);
    return user ? `${user.firstName} ${user.lastName}` : 'Unknown User';
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">My Dashboard</h1>
          <p className="text-muted-foreground">
            View your account and transaction history
          </p>
        </div>
        {canTransfer && familyMembers.length > 1 && !transfersError && (
          <TransferForm 
            children={familyMembers}
            currentUserId={currentUser.id}
            currentUserRole={currentUser.role}
            onTransfer={handleTransfer}
          />
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <ChildBalanceCard balance={currentUser.balance} />
        
        {transfersError && (
          <TransferStatusCard />
        )}
      </div>
      
      <div className="space-y-6">
        {pendingTransfers.length > 0 && (
          <div>
            <h2 className="text-xl font-bold mb-4">Pending Transfers</h2>
            <div className="space-y-4 mb-6">
              {pendingTransfers.map((transfer) => (
                <div key={transfer.id} className="rounded-lg border p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">
                        {transfer.fromUserId === currentUser.id 
                          ? `To: ${getUserName(transfer.toUserId)}`
                          : `From: ${getUserName(transfer.fromUserId)}`
                        }
                      </p>
                      <p className="text-sm text-muted-foreground m-0 p-0">
                        {formatCurrency(transfer.amount)}
                      </p>
                      {transfer.description && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {transfer.description}
                        </p>
                      )}
                    </div>
                    <div className="px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                      Pending
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div>
          <h2 className="text-xl font-bold mb-4">Recent Transactions</h2>
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-shekel-600"></div>
            </div>
          ) : (
            <TransactionList 
              transactions={transactions} 
              isAdmin={false}
            />
          )}
        </div>
        
        <TransferHistorySection 
          transfers={transfers}
          currentUser={currentUser}
          transfersError={transfersError}
        />
      </div>
    </div>
  );
}
